//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import * as React from 'react';
import { connect } from 'react-redux';
import Redux, { bindActionCreators } from 'redux';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import MultiTermHelpIcon from '@components/atoms/help-icon/MultiTermHelpIcon';
import { instagramPostUploaderActions } from '@instagram-post-uploader/actions/instagramPostUploaderActions';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ICuralateStore } from '@models/AppState';
import InlineLoadingSpinner from '@share/components/content/loading-spinner/InlineLoadingSpinner';
import { debounce } from '@utils/debounce';
import validateInstagramPermalink from '@utils/validateInstagramPermalink';

function mapStateToProps(state: ICuralateStore) {
  return {
    errorMessage: state.instagramPostUploader.errorMessage,
    isModalOpen: state.instagramPostUploader.isModalOpen,
    isPostUrlValid: state.instagramPostUploader.isPostUrlValid,
    isUploadingPost: state.instagramPostUploader.isUploadingPost,
    urlToUpload: state.instagramPostUploader.urlToUpload,
  };
}

function mapDispatchToProps(dispatch: Redux.Dispatch) {
  return {
    actions: {
      postUploader: bindActionCreators(instagramPostUploaderActions, dispatch),
      toastNotifications: bindActionCreators(
        toastNotificationsActions,
        dispatch,
      ),
    },
  };
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

class InstagramPostUploadForm extends React.Component<Props> {
  private readonly INPUT_DEBOUNCE_TIMEOUT = 300;
  private readonly PLACEHOLDER_INSTAGRAM_POST_URL =
    'https://www.instagram.com/p/#MYPOSTID#';

  public handleInputValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    const isValid = validateInstagramPermalink(value);
    this.props.actions.postUploader.validatePostUrlValue(isValid);

    this.props.actions.postUploader.updatePostUrlValue(value);
  };

  public debouncedHandleInputValue = debounce(
    this.handleInputValue,
    this.INPUT_DEBOUNCE_TIMEOUT,
  );

  public handleCheckInputValueForErrors = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.currentTarget.value;

    if (!validateInstagramPermalink(value)) {
      this.props.actions.postUploader.updateErrorMessage(
        `The URL must be an Instagram Post. Video and Gallery Posts are not supported.
        It should look similar to this example: ${this.PLACEHOLDER_INSTAGRAM_POST_URL}`,
      );
    }
  };

  public handleAddPost = async () => {
    const { actions, urlToUpload } = this.props;

    try {
      await actions.postUploader.addPost(urlToUpload);
      actions.toastNotifications.addToastNotification({
        title: 'Media Successfully Added',
      });
      actions.postUploader.closeInstagramPostUploaderModal();
    } catch (err) {
      actions.postUploader.updateErrorMessage(
        'Instagram Post failed to upload.',
      );
    }
  };

  public render() {
    const { actions, errorMessage, isPostUrlValid, isUploadingPost } =
      this.props;

    return (
      <>
        <label>
          Instagram Post URL
          <CustomIcon
            placement='bottom'
            helpTerms={[
              {
                helpText:
                  'You can only paste URLs from Instagram Posts. Video and Gallery Posts are not supported.',
              },
            ]}
          />
        </label>
        <FormWrapper>
          <Input
            className='cu-form-control'
            onBlur={this.handleCheckInputValueForErrors}
            onChange={this.handleInputValue}
            onFocus={actions.postUploader.resetErrorMessage}
            placeholder={this.PLACEHOLDER_INSTAGRAM_POST_URL}
          />
          <PrimaryButton
            isDisabled={!isPostUrlValid}
            onClick={this.handleAddPost}
          >
            Add
            <InlineLoadingSpinner
              shouldShowSpinner={isUploadingPost}
              className='cu-ml-2'
            />
          </PrimaryButton>
        </FormWrapper>
        {!!errorMessage && (
          <Error className='cu-color-error'>{errorMessage}</Error>
        )}
      </>
    );
  }
}

const CustomIcon = styled(MultiTermHelpIcon)`
  && {
    vertical-align: inherit;
    padding-left: ${({ theme }) => theme.spacings[1]};
  }
`;

const FormWrapper = styled.div`
  display: flex;
`;

const Input = styled.input`
  flex: 1 0 auto;
  margin-right: ${({ theme }) => theme.spacings[2]};
`;

const Error = styled.div`
  margin-top: ${({ theme }) => theme.spacings[2]};
`;

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(InstagramPostUploadForm);
